<template>
  <div class="abnormal-order-dialog">
    <el-dialog
      :visible.sync="dialogVisible"
      :modal-append-to-body="true"
      :append-to-body="true"
      custom-class="abnormal-order-dialog__panel"
      width="760px"
      top="8vh"
      :close-on-click-modal="false"
      @open="handleOpen"
      @closed="handleClosed"
    >
      <span slot="title" class="dialog-title">
        <i class="el-icon-warning-outline"></i>
        <span>{{ $t('order.handleAbnormalDialogTitle') }}</span>
      </span>
      <div v-loading="videoLoading" class="abnormal-order-dialog__body">
        <div class="section-block section-block--compact">
          <el-form label-position="top" size="small">
            <el-row :gutter="16">
              <el-col :span="12">
                <el-form-item :label="$t('order.orderNumber')">
                  <el-input :value="orderInfo.orderNumber || '-'" disabled></el-input>
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item :label="$t('order.approvalStatus')">
                  <el-select
                    v-model="form.approvalStatus"
                    :placeholder="$t('order.approvalStatusPlaceholder')"
                    style="width: 100%;"
                  >
                    <el-option
                      v-for="item in normalizedApprovalOptions"
                      :key="item.value"
                      :label="item.label"
                      :value="item.value"
                    ></el-option>
                  </el-select>
                </el-form-item>
              </el-col>
            </el-row>
          </el-form>
        </div>

        <div class="section-block">
          <div class="section-block__title">
            <i class="el-icon-video-camera"></i>
            <span>{{ $t('order.openDoorVideo') }}</span>
          </div>
          <div class="video-wrap">
            <div
              v-for="video in videoList"
              :key="video.id"
              class="video-item"
              @click="previewVideo(video)"
            >
              <img
                v-if="videoPosterMap[video.resourceUrl]"
                :src="videoPosterMap[video.resourceUrl]"
                alt=""
                class="video-item__poster"
              />
              <video
                v-else
                class="video-item__player"
                :src="video.resourceUrl"
                muted
                playsinline
                preload="auto"
                @loadedmetadata="handleVideoThumbnailLoaded"
              ></video>
              <div class="video-item__icon">
                <i class="el-icon-video-play"></i>
              </div>
            </div>
            <el-empty v-if="!videoList.length" :description="$t('common.noData')"></el-empty>
          </div>
        </div>

        <div v-if="shouldReviewProducts" class="section-block">
          <div class="section-block__header">
            <span class="section-block__title">
              <i class="el-icon-goods"></i>
              <span>{{ $t('order.reviewProducts') }}</span>
            </span>
            <el-button type="primary" plain size="mini" @click="showChooseProduct = true">
              {{ $t('order.chooseProducts') }}
            </el-button>
          </div>
          <div class="product-change__list">
            <div
              v-for="item in form.productDTOS"
              :key="item.openDoorAisleGoodsId || item.goodsId"
              class="product-change__item"
            >
              <div class="product-change__thumb">
                <img :src="item.url" alt="" />
              </div>
              <div class="product-change__main">
                <div class="product-change__item-name">{{ item.goodsName }}</div>
                <el-tag size="mini" type="info">{{ $t('order.buyNum') }}: {{ item.buyNum }}</el-tag>
              </div>
            </div>
            <el-empty v-if="!form.productDTOS.length" :description="$t('common.noData')"></el-empty>
          </div>
        </div>
      </div>

      <span slot="footer" class="dialog-footer">
        <el-button size="small" @click="dialogVisible = false">{{ $t('common.cancel') }}</el-button>
        <el-button type="primary" size="small" :loading="submitting" @click="submit">
          {{ $t('common.definite') }}
        </el-button>
      </span>
    </el-dialog>

    <el-dialog
      :visible.sync="videoPreviewVisible"
      :modal-append-to-body="true"
      :append-to-body="true"
      width="720px"
      :close-on-click-modal="false"
    >
      <span slot="title" class="dialog-title">
        <i class="el-icon-film"></i>
        <span>{{ $t('order.videoPreview') }}</span>
      </span>
      <video v-if="previewResourceUrl" :src="previewResourceUrl" controls width="100%"></video>
      <span slot="footer" class="dialog-footer">
        <el-button size="small" @click="videoPreviewVisible = false">{{ $t('common.cancel') }}</el-button>
      </span>
    </el-dialog>

    <el-dialog
      :visible.sync="showChooseProduct"
      :modal-append-to-body="true"
      :append-to-body="true"
      custom-class="abnormal-order-dialog__panel"
      width="960px"
      :close-on-click-modal="false"
      @open="loadProducts"
    >
      <span slot="title" class="dialog-title">
        <i class="el-icon-collection-tag"></i>
        <span>{{ $t('order.chooseProducts') }}</span>
      </span>
      <div class="product-selector">
        <div class="section-block section-block--compact">
          <div class="section-block__title">
            <i class="el-icon-box"></i>
            <span>{{ $t('order.availableProducts') }}</span>
          </div>
          <div v-loading="productLoading" class="product-selector__available">
            <div class="product-grid">
              <div
                v-for="item in productList"
                :key="item.openDoorAisleGoodsId"
                class="product-grid__item"
                @click="addProduct(item)"
              >
                <div class="product-grid__image">
                  <img :src="item.url" alt="" />
                </div>
                <div class="product-grid__name">{{ item.goodsName }}</div>
                <div class="product-grid__price">${{ item.sellingPrice }}</div>
              </div>
            </div>
            <el-empty v-if="!productLoading && !productList.length" :description="$t('common.noData')"></el-empty>
          </div>
        </div>

        <div class="section-block section-block--compact">
          <div class="section-block__title">
            <i class="el-icon-shopping-bag-1"></i>
            <span>{{ $t('order.selectedProducts') }}</span>
          </div>
          <div
            :class="[
              'product-selector__selected',
              { 'product-selector__selected--empty': !selectedProducts.length }
            ]"
          >
            <div
              v-for="item in selectedProducts"
              :key="item.openDoorAisleGoodsId"
              class="product-grid__item product-grid__item--selected"
            >
              <div class="product-grid__remove" @click="removeProduct(item)">
                <i class="el-icon-close"></i>
              </div>
              <div class="product-grid__image">
                <img :src="item.url" alt="" />
              </div>
              <div class="product-grid__name">{{ item.goodsName }}</div>
              <div class="product-grid__price">${{ item.sellingPrice }}</div>
              <el-input-number v-model="item.buyNum" :min="1" :step="1" size="small"></el-input-number>
            </div>
            <el-empty v-if="!selectedProducts.length" :description="$t('common.noData')"></el-empty>
          </div>
        </div>
      </div>

      <span slot="footer" class="dialog-footer">
        <el-button size="small" @click="showChooseProduct = false">{{ $t('common.cancel') }}</el-button>
        <el-button type="primary" size="small" @click="confirmProducts">{{ $t('common.definite') }}</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: "AbnormalOrderDialog",
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    orderInfo: {
      type: Object,
      default: () => ({})
    },
    approvalStatusOptions: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      videoLoading: false,
      productLoading: false,
      submitting: false,
      videoList: [],
      videoPosterMap: {},
      productList: [],
      form: {
        approvalStatus: "",
        productDTOS: []
      },
      showChooseProduct: false,
      selectedProducts: [],
      videoPreviewVisible: false,
      previewResourceUrl: ""
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible;
      },
      set(value) {
        this.$emit("update:visible", value);
      }
    },
    normalizedApprovalOptions() {
      if (this.approvalStatusOptions.length) {
        return this.approvalStatusOptions.map(item => ({
          value: Number(item.value ?? item.dictValue),
          label: item.label || item.dictLabel
        }));
      }
      return [
        { value: 1, label: this.$t("order.approvalStatus1") },
        { value: 2, label: this.$t("order.approvalStatus2") },
        { value: 3, label: this.$t("order.approvalStatus3") }
      ];
    },
    shouldReviewProducts() {
      return [1, 2].includes(Number(this.form.approvalStatus));
    }
  },
  methods: {
    handleOpen() {
      this.form = {
        approvalStatus: "",
        productDTOS: []
      };
      this.selectedProducts = [];
      this.videoList = [];
      this.videoPosterMap = {};
      this.loadVideos();
    },
    handleClosed() {
      this.videoList = [];
      this.videoPosterMap = {};
      this.productList = [];
      this.selectedProducts = [];
      this.previewResourceUrl = "";
      this.videoPreviewVisible = false;
      this.showChooseProduct = false;
      this.$emit("close");
    },
    loadVideos() {
      if (!this.orderInfo.id) {
        return;
      }
      this.videoLoading = true;
      this.$api
        .request(this.$url.OpenDoor.getOrderVideoResources, this.$method.get, {
          ordersId: this.orderInfo.id
        })
        .then(res => {
          if (res.code === 0) {
            this.videoList = Array.isArray(res.data) ? res.data : [];
            this.prepareVideoPosters(this.videoList);
          }
          this.videoLoading = false;
        })
        .catch(() => {
          this.videoLoading = false;
        });
    },
    previewVideo(video) {
      this.previewResourceUrl = video.resourceUrl || "";
      this.videoPreviewVisible = true;
    },
    prepareVideoPosters(videoList) {
      videoList.forEach(video => {
        const resourceUrl = video.resourceUrl;
        if (!resourceUrl || this.videoPosterMap[resourceUrl]) {
          return;
        }
        this.generateVideoPoster(resourceUrl).then(posterUrl => {
          if (posterUrl) {
            this.$set(this.videoPosterMap, resourceUrl, posterUrl);
          }
        });
      });
    },
    generateVideoPoster(videoUrl) {
      return new Promise(resolve => {
        if (!videoUrl) {
          resolve("");
          return;
        }

        const video = document.createElement("video");
        let settled = false;
        const finalize = posterUrl => {
          if (settled) {
            return;
          }
          settled = true;
          video.pause();
          video.removeAttribute("src");
          video.load();
          resolve(posterUrl || "");
        };
        const captureFrame = () => {
          try {
            const canvas = document.createElement("canvas");
            canvas.width = video.videoWidth || 320;
            canvas.height = video.videoHeight || 180;
            const context = canvas.getContext("2d");
            if (!context) {
              finalize("");
              return;
            }
            context.drawImage(video, 0, 0, canvas.width, canvas.height);
            finalize(canvas.toDataURL("image/jpeg", 0.82));
          } catch (error) {
            finalize("");
          }
        };

        video.crossOrigin = "anonymous";
        video.preload = "auto";
        video.muted = true;
        video.playsInline = true;
        video.onloadeddata = () => {
          const duration = Number(video.duration) || 0;
          if (duration > 0.12) {
            try {
              video.currentTime = Math.min(0.1, duration / 2);
            } catch (error) {
              captureFrame();
            }
            return;
          }
          captureFrame();
        };
        video.onseeked = captureFrame;
        video.onerror = () => finalize("");
        video.src = videoUrl;
        video.load();
        setTimeout(() => finalize(""), 5000);
      });
    },
    handleVideoThumbnailLoaded(event) {
      const player = event.target;
      const duration = Number(player.duration) || 0;
      if (duration <= 0 || player.dataset.thumbnailReady === "1") {
        return;
      }
      player.dataset.thumbnailReady = "1";
      try {
        player.currentTime = Math.min(0.1, duration / 2);
      } catch (error) {
        player.dataset.thumbnailReady = "0";
      }
    },
    loadProducts() {
      const userEquipmentId = this.orderInfo.equipmentId || this.orderInfo.userEquipmentId;
      if (!userEquipmentId) {
        this.productList = [];
        return;
      }
      this.productLoading = true;
      this.$api
        .request(this.$url.OpenDoor.getAisleGoodsConfig, this.$method.get, {
          userEquipmentId
        })
        .then(res => {
          if (res.code === 0) {
            this.productList = this.normalizeProductList(res.data);
          } else {
            this.$message.warning(res.msg);
          }
          this.productLoading = false;
        })
        .catch(() => {
          this.productLoading = false;
          this.$message.error(this.$t("order.loadProductsFailed"));
        });
    },
    normalizeProductList(data) {
      const flatList = [];
      const source = data || {};
      if (Array.isArray(source)) {
        source.forEach(item => flatList.push(item));
      } else {
        Object.keys(source).forEach(key => {
          const group = Array.isArray(source[key]) ? source[key] : [];
          group.forEach(item => flatList.push(item));
        });
      }
      const uniqueProducts = [];
      flatList.forEach(item => {
        const openDoorAisleGoodsId = item.id;
        if (!openDoorAisleGoodsId || uniqueProducts.some(product => product.openDoorAisleGoodsId === openDoorAisleGoodsId)) {
          return;
        }
        uniqueProducts.push({
          openDoorAisleGoodsId,
          goodsId: item.goodsId,
          goodsName: item.goodsName,
          url: item.url,
          sellingPrice: item.sellingPrice,
          buyNum: 1
        });
      });
      return uniqueProducts;
    },
    addProduct(product) {
      if (this.selectedProducts.some(item => item.openDoorAisleGoodsId === product.openDoorAisleGoodsId)) {
        return;
      }
      this.selectedProducts.push({
        ...product
      });
    },
    removeProduct(product) {
      this.selectedProducts = this.selectedProducts.filter(
        item => item.openDoorAisleGoodsId !== product.openDoorAisleGoodsId
      );
    },
    confirmProducts() {
      this.form.productDTOS = this.selectedProducts.map(item => ({
        openDoorAisleGoodsId: item.openDoorAisleGoodsId,
        goodsId: item.goodsId,
        goodsName: item.goodsName,
        url: item.url,
        sellingPrice: item.sellingPrice,
        buyNum: item.buyNum
      }));
      this.showChooseProduct = false;
    },
    submit() {
      if (!this.form.approvalStatus) {
        this.$message.warning(this.$t("order.approvalStatusPlaceholder"));
        return;
      }

      const payload = {
        ordersNumber: this.orderInfo.orderNumber,
        approvalStatus: this.form.approvalStatus
      };

      if (this.shouldReviewProducts && this.form.productDTOS.length === 0) {
        this.$message.warning(this.$t("order.selectReviewProductsRequired"));
        return;
      }

      if (this.shouldReviewProducts) {
        payload.productDTOS = this.form.productDTOS;
      }

      this.submitting = true;
      this.$api
        .request(this.$url.OpenDoor.approvalOrder, this.$method.post, payload)
        .then(res => {
          if (res.code === 0) {
            this.$message.success(this.$t("order.handleAbnormalSuccess"));
            this.dialogVisible = false;
            this.$emit("refresh");
          } else {
            this.$message.warning(res.msg);
          }
          this.submitting = false;
        })
        .catch(() => {
          this.submitting = false;
          this.$message.error(this.$t("order.handleAbnormalFailed"));
        });
    }
  }
};
</script>

<style scoped lang="scss">
.abnormal-order-dialog__body {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.dialog-title,
.section-block__title {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.dialog-title {
  color: #303133;
  font-size: 14px;
  font-weight: 600;
}

.dialog-title i,
.section-block__title i {
  color: #8b93a6;
}

.section-block {
  padding: 16px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  background: #fafafa;
}

.section-block--compact {
  padding-bottom: 16px;
}

.section-block__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.section-block__title {
  color: #303133;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  margin-bottom: 12px;
}

.video-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  min-height: 120px;
}

.video-item {
  position: relative;
  width: calc(33.333% - 8px);
  height: 116px;
  overflow: hidden;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  cursor: pointer;
  background: #000;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.video-item:hover {
  border-color: #d8dee8;
  box-shadow: 0 6px 18px rgba(31, 45, 61, 0.08);
}

.video-item__poster,
.video-item__player {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.video-item__player {
  display: block;
  pointer-events: none;
  background: #000;
}

.video-item__icon {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 30px;
  color: #fff;
  background: linear-gradient(180deg, rgba(14, 18, 26, 0.14), rgba(14, 18, 26, 0.3));
}

.product-change__list {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  min-height: 120px;
}

.product-change__item {
  display: flex;
  align-items: center;
  flex: 0 0 calc(50% - 6px);
  gap: 12px;
  max-width: calc(50% - 6px);
  padding: 12px;
  overflow: hidden;
  box-sizing: border-box;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  background: #fff;
}

.product-change__thumb {
  width: 72px;
  height: 72px;
  flex: 0 0 72px;
  border-radius: 4px;
  overflow: hidden;
  background: #f5f7fa;
}

.product-change__thumb img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.product-change__main {
  display: flex;
  flex: 1;
  min-width: 0;
  flex-direction: column;
  gap: 8px;
}

.product-change__item-name {
  color: #303133;
  font-size: 13px;
  line-height: 18px;
  word-break: break-word;
}

.product-selector__available {
  max-height: 360px;
  padding: 8px 0 4px;
  overflow-y: auto;
}

.product-selector {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.product-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 12px;
  align-items: start;
}

.product-grid__item {
  position: relative;
  padding: 12px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  cursor: pointer;
  background: #fff;
  text-align: center;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.product-grid__item:hover {
  border-color: #d8dee8;
  box-shadow: 0 6px 18px rgba(31, 45, 61, 0.08);
}

.product-grid__image {
  width: 136px;
  height: 136px;
  margin: 0 auto 10px;
}

.product-grid__image img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.product-grid__name {
  min-height: 36px;
  overflow: hidden;
  color: #303133;
  line-height: 18px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.product-grid__price {
  margin: 6px 0 10px;
  color: #f56c6c;
  font-weight: 600;
}

.product-selector__selected {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  min-height: 180px;
  align-items: flex-start;
  align-content: flex-start;
  padding: 8px 0 4px;
}

.product-selector__selected--empty {
  justify-content: center;
  align-items: center;
  align-content: center;
}

.product-grid__item--selected {
  width: 180px;
  cursor: default;
}

.product-grid__item--selected:hover {
  border-color: #dcdfe6;
  box-shadow: none;
}

.product-grid__remove {
  position: absolute;
  top: 8px;
  right: 8px;
  cursor: pointer;
  color: #909399;
}

::v-deep .abnormal-order-dialog__panel .el-dialog__body {
  padding: 18px 20px;
}

::v-deep .abnormal-order-dialog__panel .el-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 180px;
  margin: 0 auto;
}
</style>
